/**
 * Opening hours.
 *
 * These are not cosmetic: `private.opening_window_at` is what decides whether an
 * order can be placed at all, which scheduled pickup slots are offered, and how
 * every daily sales total is bucketed. A mistake here stops customers ordering,
 * so the screen states the consequence rather than just the times.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import {
  describeDay,
  formatDateKey,
  parseTimeToMinutes,
  type MessageKey,
  type OpeningHour,
} from '@yummy/shared';
import { deleteHoursException, fetchMerchantRestaurant, saveHoursException, saveOpeningHours } from '@yummy/db';
import {
  Button,
  Callout,
  Card,
  ConfirmDialog,
  DateField,
  EmptyState,
  ErrorBanner,
  LoadingState,
  PageHeading,
  Row,
  Screen,
  Section,
  Stack,
  SwitchField,
  Text,
  TextField,
  TimeField,
  ToneBadge,
  useAction,
  useAsync,
  useLocale,
  useTheme,
  useToast,
} from '@yummy/ui-rnr';

import { FooterBar } from '../components/FooterBar';
import { useMerchant } from '../app/auth';
import { supabase } from '../lib/supabase';
import { Clock } from 'lucide-react-native';


/**
 * The first weekday with a window that closes before it opens, or null.
 *
 * A close of `00:00` is `24:00` — the end of the day — so it is always later
 * than its open and is not a reversal. Anything else earlier than the open is
 * one: a merchant who typed the two the wrong way round, or who meant a service
 * that runs past midnight. Neither is stored any more, because the second has a
 * clearer spelling: the same trade continued as a window on the following day,
 * which is what a printed schedule does and what `item_availability_windows`
 * has always done.
 *
 * @param hours The draft week.
 * @returns The weekday index, or null when every window runs forwards.
 */
function closingBeforeOpening(hours: OpeningHour[]): number | null {
  for (const hour of hours) {
    if (hour.is_closed) continue;
    const open = parseTimeToMinutes(hour.open_time);
    const close = parseTimeToMinutes(hour.close_time);
    if (open === null || close === null) continue;
    if (close === 0) continue;
    if (close <= open) return hour.weekday;
  }
  return null;
}

/**
 * The week's rows as the table takes them.
 *
 * A closed day collapses to a single closure marker — the table allows one per
 * weekday — keeping the first window's times so that reopening the day restores
 * something rather than nothing. An open day contributes one row per window.
 *
 * @param hours The draft week.
 * @returns Rows ready for `saveOpeningHours`.
 */
function hoursPayload(hours: OpeningHour[]) {
  const byWeekday = new Map<number, OpeningHour[]>();
  for (const hour of hours) {
    byWeekday.set(hour.weekday, [...(byWeekday.get(hour.weekday) ?? []), hour]);
  }

  const rows: Array<Pick<OpeningHour, 'weekday' | 'is_closed' | 'open_time' | 'close_time'>> = [];

  for (const [weekday, dayRows] of [...byWeekday.entries()].sort((a, b) => a[0] - b[0])) {
    if (dayRows.some((row) => row.is_closed)) {
      const first = dayRows[0]!;
      rows.push({
        weekday,
        is_closed: true,
        open_time: first.open_time,
        close_time: first.close_time,
      });
      continue;
    }

    for (const row of dayRows) {
      rows.push({
        weekday,
        is_closed: false,
        open_time: row.open_time,
        close_time: row.close_time,
      });
    }
  }

  return rows;
}

/**
 * The first weekday whose own windows overlap, or null.
 *
 * Windows that merely run past midnight are not overlaps: each is measured to
 * the end it actually reaches, so a 17:30 – 02:00 service is seen to occupy the
 * evening and a second window placed inside it is caught.
 *
 * @param hours The draft week.
 * @returns The weekday index, or null when nothing overlaps.
 */
function overlappingWeekday(hours: OpeningHour[]): number | null {
  const byWeekday = new Map<number, Array<{ open: number; close: number }>>();

  for (const hour of hours) {
    if (hour.is_closed) continue;
    const open = parseTimeToMinutes(hour.open_time);
    const close = parseTimeToMinutes(hour.close_time);
    if (open === null || close === null) continue;

    const windows = byWeekday.get(hour.weekday) ?? [];
    windows.push({ open, close: close <= open ? close + 1440 : close });
    byWeekday.set(hour.weekday, windows);
  }

  for (const [weekday, windows] of byWeekday) {
    const sorted = [...windows].sort((a, b) => a.open - b.open);
    for (let index = 1; index < sorted.length; index += 1) {
      if (sorted[index]!.open < sorted[index - 1]!.close) return weekday;
    }
  }

  return null;
}

/**
 * Edits the weekly opening hours and the date overrides.
 *
 * @returns The opening-hours screen.
 */
export function HoursScreen() {
  const theme = useTheme();
  const { t, locale } = useLocale();
  const toast = useToast();
  const { restaurant } = useMerchant();
  const restaurantId = restaurant?.id ?? null;

  const data = useAsync(
    async () => (restaurantId ? fetchMerchantRestaurant(supabase, restaurantId) : null),
    [restaurantId],
  );

  const action = useAction();
  const [hours, setHours] = useState<OpeningHour[]>([]);
  const [dirty, setDirty] = useState(false);

  // New exception form.
  const [newDate, setNewDate] = useState('');
  const [newClosed, setNewClosed] = useState(true);
  const [newOpen, setNewOpen] = useState('10:00');
  const [newClose, setNewClose] = useState('21:00');
  const [newNote, setNewNote] = useState('');
  const [removing, setRemoving] = useState<string | null>(null);

  useEffect(() => {
    if (data.data) setHours(data.data.opening_hours);
  }, [data.data]);

  const timezone = data.data?.timezone ?? restaurant?.timezone ?? 'UTC';

  /**
   * The week as the cards render it: one entry per weekday, carrying that
   * day's windows in order. A day is several rows now, so mapping the raw list
   * would print Monday once per window.
   */  const days = useMemo(() => {
    const byWeekday = new Map<number, OpeningHour[]>();
    for (const hour of hours) {
      byWeekday.set(hour.weekday, [...(byWeekday.get(hour.weekday) ?? []), hour]);
    }
    return [...byWeekday.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([weekday, rows]) => ({ weekday, rows }));
  }, [hours]);

  /** Edits the window at `index` within a weekday, counted across that day. */
  const updateWindow = useCallback(
    (weekday: number, index: number, patch: Partial<OpeningHour>) => {
      setHours((current) => {
        let seen = -1;
        return current.map((hour) => {
          if (hour.weekday !== weekday) return hour;
          seen += 1;
          return seen === index ? { ...hour, ...patch } : hour;
        });
      });
      setDirty(true);
    },
    []
  );

  /**
   * Closes or reopens a day.
   *
   * Every row of the day is marked, not replaced by a single marker: the draft
   * keeps its windows so reopening the day before saving brings them all back.
   * The closure is collapsed to one row on the way out, which is the shape the
   * table accepts.
   */
  const setDayClosed = useCallback((weekday: number, closed: boolean) => {
    setHours((current) => current.map((h) => (h.weekday === weekday ? { ...h, is_closed: closed } : h)));
    setDirty(true);
  }, []);

  const addWindow = useCallback((weekday: number) => {
    // A dinner service, which is the second window almost every split day
    // wants. The merchant edits it if they meant something else.
    setHours((current) => [
      ...current,
      { weekday, is_closed: false, open_time: '17:30', close_time: '22:00' },
    ]);
    setDirty(true);
  }, []);

  const removeWindow = useCallback((weekday: number, index: number) => {
    setHours((current) => {
      let seen = -1;
      return current.filter((hour) => {
        if (hour.weekday !== weekday) return true;
        seen += 1;
        return seen !== index;
      });
    });
    setDirty(true);
  }, []);

  const save = useCallback(async () => {
    if (!restaurantId) return;

    // A window that closes before it opens is not stored, and neither is the
    // one it was probably meant to be. Both mistakes are caught here, where the
    // sentence can name the day.
    const backwards = closingBeforeOpening(hours);
    if (backwards !== null) {
      toast.show(
        t('merchant.hours.closesBeforeItOpens', {
          day: t(`merchant.hours.weekdayLong${(backwards + 1) % 7}` as MessageKey),
        }),
        { tone: 'danger' },
      );
      return;
    }

    const clash = overlappingWeekday(hours);
    if (clash !== null) {
      toast.show(
        t('merchant.hours.overlappingWindows', {
          day: t(`merchant.hours.weekdayLong${clash}` as MessageKey),
        }),
        { tone: 'danger' },
      );
      return;
    }

    const result = await action.run(() =>
      saveOpeningHours(supabase, restaurantId, hoursPayload(hours)),
    );

    if (result !== null || !action.error) {
      setDirty(false);
      toast.show(t('merchant.hours.saved'), { tone: 'success' });
      data.reload();
    }
  }, [restaurantId, hours, action, toast, data, t]);

  const addException = useCallback(async () => {
    if (!restaurantId || !/^\d{4}-\d{2}-\d{2}$/.test(newDate)) {
      toast.show(t('merchant.hours.invalidDate'), { tone: 'danger' });
      return;
    }

    // A one-off that closes before it opens is refused for the same reason the
    // weekly ones are: a service that runs past midnight belongs on the date it
    // reaches, not on this one with an earlier end.
    const exceptionOpen = parseTimeToMinutes(newOpen);
    const exceptionClose = parseTimeToMinutes(newClose);
    if (
      exceptionClose !== null &&
      exceptionClose !== 0 &&
      exceptionOpen !== null &&
      exceptionClose <= exceptionOpen
    ) {
      toast.show(t('merchant.hours.closesBeforeItOpensOneOff'), { tone: 'danger' });
      return;
    }

    const result = await action.run(() =>
      saveHoursException(supabase, restaurantId, {
        on_date: newDate,
        is_closed: newClosed,
        open_time: newClosed ? null : newOpen,
        close_time: newClosed ? null : newClose,
        note: newNote.trim() || null,
      }),
    );

    if (result !== null || !action.error) {
      setNewDate('');
      setNewNote('');
      toast.show(t('merchant.hours.exceptionSaved'), { tone: 'success' });
      data.reload();
    }
  }, [restaurantId, newDate, newClosed, newOpen, newClose, newNote, action, toast, data, t]);

  const removeException = useCallback(async () => {
    if (!restaurantId || !removing) return;
    const result = await action.run(() => deleteHoursException(supabase, restaurantId, removing));
    if (result !== null || !action.error) {
      setRemoving(null);
      toast.show(t('merchant.hours.exceptionRemoved'), { tone: 'neutral' });
      data.reload();
    }
  }, [restaurantId, removing, action, toast, data, t]);

  // ---------------------------------------------------------------------------

  if (!restaurantId) {
    return (
      <Screen>
        <EmptyState icon={Clock} title={t('merchant.hours.noRestaurant')} />
      </Screen>
    );
  }

  if (data.initial) {
    return (
      <Screen>
        <LoadingState message={t('merchant.hours.loading')} />
      </Screen>
    );
  }

  if (data.error || !data.data) {
    return (
      <Screen>
        <ErrorBanner message={data.error ?? t('merchant.settings.loadFailed')} onRetry={data.reload} />
      </Screen>
    );
  }

  // Days, not rows: a day with two windows is still one open day.
  const openDays = days.filter(({ rows }) => !rows.some((row) => row.is_closed)).length;

  // The one-off form's window, for the same disclosure the weekly cards give.
  const newOpenMinutes = parseTimeToMinutes(newOpen);
  const newCloseMinutes = parseTimeToMinutes(newClose);
  const newCrosses =
    newOpenMinutes !== null &&
    newCloseMinutes !== null &&
    newCloseMinutes !== 0 &&
    newCloseMinutes <= newOpenMinutes;

  return (
    <Screen
      constrain
      footer={
        <FooterBar>
          <Row gap="sm" justify="end">
                <Button
                  onPress={() => void save()}
                  disabled={action.pending || !dirty}
                  accessibilityLabel={t('merchant.hours.saveHours')}
                  aria-busy={action.pending}>
                  {action.pending ? (
                    <ActivityIndicator color={theme.palette.primaryForeground} size="small" />
                  ) : (
                    <Text>{t('merchant.hours.saveHours')}</Text>
                  )}
                </Button>
                {dirty ? (
                  <Text className="text-warning self-center text-sm">{t('merchant.hours.unsaved')}</Text>
                ) : null}
              </Row>
        </FooterBar>
      }>
      <Stack gap="xl">
        <PageHeading
          title={t('merchant.hours.title')}
          subtitle={t('merchant.hours.intro', { timezone })}
        />

        {openDays === 0 ? (
          <Callout
            tone="danger"
            title={t('merchant.hours.closedEveryDay')}
            message={t('merchant.hours.closedEveryDayHint')}
          />
        ) : null}

        {action.error ? <ErrorBanner message={action.error} onRetry={action.clearError} /> : null}

        <Section title={t('merchant.hours.weekly')}>
          <Stack gap="md">
            {days.map(({ weekday, rows }) => {
              const closed = rows.some((row) => row.is_closed);

              return (
                <Card key={weekday} className="p-4">
                  <Stack gap="md">
                    <Row spread gap="md" align="center">
                      <Stack gap="xs">
                        <Text className="text-base font-semibold">
                          {t(`merchant.hours.weekdayLong${weekday}` as MessageKey)}
                        </Text>
                        {/* Every window the day has: a lunch and a dinner read
                            as one line rather than one line per row. */}
                        <Text className="text-subtle-foreground text-xs">
                          {describeDay(rows) ?? t('common.closed')}
                        </Text>
                      </Stack>
                      <SwitchField
                        label={closed ? t('common.closed') : t('common.open')}
                        value={!closed}
                        onValueChange={(open) => setDayClosed(weekday, !open)}
                      />
                    </Row>

                    {!closed ? (
                      <Stack gap="sm">
                        {rows.map((row, index) => {
                          const openMinutes = parseTimeToMinutes(row.open_time);
                          const closeMinutes = parseTimeToMinutes(row.close_time);
                          // A close at or before its open is not a mistake by
                          // itself — it is how a service running past midnight
                          // is written — but it is also what a merchant gets by
                          // mistyping the two the wrong way round, and nothing
                          // so far said which of the two they had made. This
                          // says it, live, for the window in front of them.
                          const crosses =
                            openMinutes !== null &&
                            closeMinutes !== null &&
                            closeMinutes !== 0 &&
                            closeMinutes <= openMinutes;

                          return (
                            <Stack key={`${weekday}-${index}`} gap="xs">
                              {/* `align="start"`: the fields must sit level with
                                  each other and with a Remove button that has no
                                  label. */}
                              <Row gap="md" wrap align="start">
                                <View className="grow basis-[150px]">
                                  <TimeField
                                    label={t('merchant.hours.opens')}
                                    value={(row.open_time ?? '09:00').slice(0, 5)}
                                    onChange={(value) =>
                                      updateWindow(weekday, index, { open_time: value })
                                    }
                                  />
                                </View>
                                <View className="grow basis-[150px]">
                                  <TimeField
                                    label={t('merchant.hours.closes')}
                                    value={(row.close_time ?? '21:00').slice(0, 5)}
                                    onChange={(value) =>
                                      updateWindow(weekday, index, { close_time: value })
                                    }
                                    midnightMeansEndOfDay
                                  />
                                </View>
                                {rows.length > 1 ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="self-end"
                                    onPress={() => removeWindow(weekday, index)}
                                    accessibilityLabel={t('merchant.hours.removeWindow')}>
                                    <Text>{t('merchant.hours.removeWindow')}</Text>
                                  </Button>
                                ) : null}
                              </Row>

                              {crosses ? (
                                <Text className="text-destructive text-xs">
                                  {t('merchant.hours.closesBeforeItOpens', {
                                    day: t(
                                      `merchant.hours.weekdayLong${(weekday + 1) % 7}` as MessageKey,
                                    ),
                                  })}
                                </Text>
                              ) : null}
                            </Stack>
                          );
                        })}

                        <Button
                          variant="ghost"
                          size="sm"
                          className="self-start"
                          onPress={() => addWindow(weekday)}
                          accessibilityLabel={t('merchant.hours.addWindow')}>
                          <Text>{t('merchant.hours.addWindow')}</Text>
                        </Button>
                      </Stack>
                    ) : null}
                  </Stack>
                </Card>
              );
            })}
          </Stack>

        </Section>

        <Section
          title={t('merchant.hours.exceptions')}
          subtitle={t('merchant.hours.exceptionsHint')}
        >
          <Stack gap="md">
            {data.data.exceptions.length === 0 ? (
              <Text className="text-muted-foreground text-sm">
                {t('merchant.hours.noExceptions')}
              </Text>
            ) : (
              data.data.exceptions.map((exception) => (
                <Card key={exception.on_date} className="p-4">
                  <Row spread gap="md" align="center">
                    <Stack gap="xs" className="flex-1">
                      <Row gap="sm" align="center" wrap>
                        <Text className="text-base font-bold">
                          {formatDateKey(exception.on_date, 'weekday', locale)}{' '}
                          {formatDateKey(exception.on_date, 'short', locale)}
                        </Text>
                        {exception.is_closed ? (
                          <ToneBadge label={t('common.closed')} tone="danger" size="sm" />
                        ) : (
                          <ToneBadge
                            label={`${(exception.open_time ?? '').slice(0, 5)} – ${(exception.close_time ?? '').slice(0, 5)}`}
                            tone="info"
                            size="sm"
                          />
                        )}
                      </Row>
                      {exception.note ? (
                        <Text className="text-muted-foreground text-sm">{exception.note}</Text>
                      ) : null}
                    </Stack>
                    <Button
                      size="sm"
                      variant="ghost"
                      onPress={() => setRemoving(exception.on_date)}
                      accessibilityLabel={t('common.remove')}>
                      <Text>{t('common.remove')}</Text>
                    </Button>
                  </Row>
                </Card>
              ))
            )}
          </Stack>
        </Section>

        <Section title={t('merchant.hours.addException')}>
          <Card className="p-4">
            <Stack gap="lg">
              <DateField label={t('common.date')} value={newDate} onChange={setNewDate} />
              <SwitchField
                label={t('merchant.hours.closedAllDay')}
                description={t('merchant.hours.closedAllDayHint')}
                value={newClosed}
                onValueChange={setNewClosed}
              />
              {!newClosed ? (
                <Stack gap="xs">
                  <Row gap="md" wrap align="start">
                    <View className="grow basis-[160px]">
                      <TimeField
                        label={t('merchant.hours.opens')}
                        value={newOpen}
                        onChange={setNewOpen}
                      />
                    </View>
                    <View className="grow basis-[160px]">
                      <TimeField
                        label={t('merchant.hours.closes')}
                        value={newClose}
                        onChange={setNewClose}
                        midnightMeansEndOfDay
                      />
                    </View>
                  </Row>
                  {/* The same disclosure the weekly cards give: a close at or
                      before the open is a service running past midnight. The
                      date a one-off runs into is the customer's to work out, so
                      this states the rule rather than naming the day. */}
                  {newCrosses ? (
                    <Text className="text-destructive text-xs">
                      {t('merchant.hours.closesBeforeItOpensOneOff')}
                    </Text>
                  ) : null}
                </Stack>
              ) : null}
              <TextField
                label={t('common.note')}
                value={newNote}
                onChangeText={setNewNote}
                placeholder={t('merchant.hours.exceptionNotePlaceholder')}
              />
              <Button
                variant="secondary"
                onPress={() => void addException()}
                accessibilityLabel={t('merchant.hours.addExceptionButton')}>
                <Text>{t('merchant.hours.addExceptionButton')}</Text>
              </Button>
            </Stack>
          </Card>
        </Section>
      </Stack>

      <ConfirmDialog
        visible={removing !== null}
        title={t('merchant.hours.removeTitle')}
        message={t('merchant.hours.removeMessage')}
        confirmLabel={t('common.remove')}
        destructive
        busy={action.pending}
        onConfirm={() => void removeException()}
        onCancel={() => setRemoving(null)}
      />
    </Screen>
  );
}
